<template>
  <oct-dialog
    full
    :title="$route.meta.title"
    icon="arrow-left"
    @dismiss="$_octPrototypeView_goBack"
    class="ss-customer"
  >
    <div>
      <div class="ss-customer-intro">
        <!-- Customer card. Customer may not exist -->
        <oct-customer-card
          :icon="customer ? customer.profileIcon : undefined"
          :title="customer ? `${customer.name} 様` : ''"
        />

        <!-- Secret checkbox -->
        <oct-checkbox
          v-model="$_ssCustomerView_isSecret"
          class="ss-customer__secret"
          label="検索に表示しない"
          :key="Date.now()"
          v-if="customer"
        />
      </div>

      <div class="ss-customer__header">
        <span class="ss-customer__primary-text">
          過去1年間に{{ collection.items.length }}件の施術履歴があります
        </span>
      </div>

      <div class="ss-customer__content">
        <div class="ss-customer-history">
          <ul class="ss-customer-history-list">
            <li
              v-for="item in collection.items"
              :key="item.id"
              @click="$router.push({
                path: `../${item.id}`,
              }).catch(() => {})"
              class="ss-customer-history-list__item"
            >
              <div class="ss-customer-history__content">
                <!-- Main Content -->
                <div class="ss-customer-history__body">
                  <!-- Image -->
                  <oct-media
                    :image="item.images.length ? item.images[0] : {
                      lowResolution: '/images/customer__default.png',
                      standardResolution: '/images/customer__default.png',
                      highResolution: '/images/customer__default.png'
                    }"
                    :alt="customer ? customer.name : ''"
                    class="ss-customer-history__media"
                  />

                  <!-- Text & Products -->
                  <div class="ss-customer-history__text">
                    <!-- Date -->
                    <span class="ss-customer-history__primary-text">
                      {{ $_octPrototypeView_getDateString(item.publishedAt) }}
                    </span>

                    <!-- Menus & Products-->
                    <ul class="ss-customer-history-products">
                      <!-- Menus -->
                      <li
                        class="ss-customer-history-menus__item"
                        v-for="menu in item.menus"
                        :key="menu.id"
                      >
                        <span class="ss-customer-history-menus__text">
                          {{ menu.title }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Chip -->
                <div class="ss-customer-history__meta">
                  <!-- Like Icon-->
                  <div
                    class="ss-customer-history__like"
                    :class="{
                      'ss-customer-history__like--active': item.likedAt !== null
                    }"
                  >
                    <oct-icon
                      icon="favorite-filled"
                    />
                  </div>

                  <oct-chip
                    dense
                    :selected="item.status != 4"
                  >
                    {{ item.status != 4 ? '未送信' : '送信済' }}
                  </oct-chip>

                  <!-- Trailing Icon -->
                  <oct-icon
                    icon="chevron-right"
                    class="ss-customer-history__trailing-icon"
                  />
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Action -->
    <template #actions>
      <oct-button
        block
        size="large"
        @click.native="addNewTreatment"
      >
        新規施術追加
      </oct-button>
    </template>
  </oct-dialog>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue'
import OctButton from '@/components/button/OctButton.vue'
import OctCheckbox from '@/components/checkbox/OctCheckbox.vue'
import OctChip from '@/components/chips/OctChip.vue'
import OctMedia from '@/components/media/OctMedia.vue'
import OctCustomerCard from '@/components/customer-card/OctCustomerCard.vue'
import OctDialog from '@/components/dialog/OctDialog.vue'
import OctPrototypeView from '@/mixins/OctPrototypeView.js'

export default {
  name: 'SSCustomerView',
  mixins: [
    OctPrototypeView
  ],
  components: {
    OctIcon,
    OctButton,
    OctCheckbox,
    OctChip,
    OctMedia,
    OctCustomerCard,
    OctDialog
  },
  props: {
    /** Customer ID */
    customerId: {
      type: String,
      default: undefined
    }
  },
  computed: {
    /** Customer itself */
    customer: vm => vm.$store.getters['customers/byId'](vm.customerId),
    /** Customer’s recent treatments */
    collection: vm => vm.$store.getters['treatments/collection'](vm.customerId),
    /**
     * Whether the customer is set as secret (hidden from search results)
     */
    $_ssCustomerView_isSecret: {
      /**
       * Get the current secret status of the customer
       * @returns {boolean} True if the customer is set as secret
       */
      get () {
        return this.$store.getters.isSecretCustomer(this.customerId);
      },
      /**
       * Set the secret status of the customer
       * @param {boolean} value - True to hide the customer from search results, false to show
       */
      async set (value) {
        // Do nothing if the value is the same
        if (value === this.$_ssCustomerView_isSecret) return;

        // Show confirmation dialog if user is trying to set the customer as secret
        if (value) {
          // Check if the number of secret customers is already 100
          if (this.$store.state.data.secretCustomers.length >= 100) {
            this.$store.dispatch('alert', '検索結果非表示の登録数が100件を超えています')
            // Reset checkbox state
            this.$forceUpdate()
            return;
          }

          try {
            await this.$store.dispatch('confirm', '検索結果に表示されなくなりますが、よろしいですか？')
          } catch {
            // If confirmation is not accepted, stop further processing
            return;
          }
        }

        // Update secret customers
        const secretCustomers = value
          ? this.$store.state.data.secretCustomers.some(customer => customer.id === this.customerId)
            ? this.$store.state.data.secretCustomers // Already exists, no need to add
            : this.$store.state.data.secretCustomers.concat({id: this.customerId})
          : this.$store.state.data.secretCustomers.filter(customer => customer.id !== this.customerId);
        
        // Do update
        try {
          await this.$store.dispatch(
            'updateSalonData',
            {
              ...this.$store.state.data,
              secretCustomers
            }
          );
        } catch {
          this.$store.dispatch('alert', '検索結果表示設定を更新できませんでした')
          // Force Vue to re-evaluate the computed property to restore the checkbox state
          this.$forceUpdate()
        }
      }
    }
  },
  methods: {
    /**
     * Get treated products
     * @param {object} item - A treatment object
     */
    getProducts (item) {
      return [item.shampoo, item.treatment, item.outbath].concat(item.others)
        .map(p => (
          p ? this.$store.getters['products/byId'](p) : undefined 
        ))
        .filter(p => p)
    },
    /** Add new treatment */
    addNewTreatment () {
      // Add new treatment
      this.$store.commit('treatments/add', {
        items: [{
          id: 'new',
          salon: this.$store.state.id,
          customer: this.customerId
        }]
      })

      // Go to stylist selector
      this.$router.push({
        name: 'Stylists.todays'
      }).catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
@import "../components/theme/variables";
@import "../components/grid/variables";
@import "../components/typography/variables";
@import "../components/typography/mixins";

.ss-customer {
  transform: translateZ(0);

  &::v-deep .oct-dialog-header {
    margin-bottom: oct-rem(16);
  }
}

.ss-customer-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: oct-rem(16);
  margin-bottom: oct-rem(32);

  &::v-deep .oct-customer-card__media {
    margin-bottom: oct-rem(32);
  }

  &::v-deep .oct-checkbox__background {
    margin-right: oct-rem(10);
  }
}

.ss-customer__header {
  text-align: center;
  margin: 0 0 oct-rem(32);
}

.ss-customer__primary-text {
  font-size: oct-rem(16);
  line-height: 1;
  color: $oct-theme--neutral-50;
  display: block;
}

.ss-customer-history-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ss-customer-history-list__item {
  margin: 0 0 oct-rem(16);
  cursor: pointer;
}

.ss-customer-history__content {
  display: flex;
  justify-content: space-between;
}

.ss-customer-history__body {
  display: flex;
  align-items: flex-start;
  gap: oct-rem(16);
  padding-right: oct-rem(16);
}

.ss-customer-history__media {
  flex: 0 0 oct-rem(64);
  width: oct-rem(64);
  max-width: oct-rem(64);
  padding-top: oct-rem(64)
}

.ss-customer-history__primary-text {
  font-size: oct-rem(12);
  line-height: 1;
  color: $oct-theme--neutral-50;
  display: block;
  margin: 0 0 oct-rem(12);
}

.ss-customer-history-products {
  list-style: none;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: oct-rem(4);
  min-height: oct-rem(32);
  padding: 0;
}

.ss-customer-history-menus__item {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: oct-rem(32);
  padding: 0 oct-rem(8);
  border: 1px solid $oct-theme--neutral-20;
  border-radius: oct-rem(4);
}

.ss-customer-history-menus__text {
  font-size: oct-rem(10);
  font-weight: bold;
  line-height: 1;
  color: $oct-theme--neutral-50;
}

.ss-customer-history-products__item {
  flex: 0 0 oct-rem(32);
  width: oct-rem(32);
  max-width: oct-rem(32);
  padding: oct-rem(4);
  border: 1px solid $oct-theme--neutral-20;
  border-radius: oct-rem(4);

  &--more {
    display: flex;
    align-items: center;
    border-width: 0;

    .oct-icon {
      width: oct-rem(12.5);
    }
  }
}

.ss-customer-history__meta {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: oct-rem(4);
  height: oct-rem(48);

  .oct-chip {
    flex: 0 0 auto;
  }
}

.ss-customer-history__like {
  margin: 0 oct-rem(8) 0 0;
  display: flex;
  justify-content: center;

  .oct-icon {
    fill: $oct-theme--neutral-30;
    width: oct-rem(16);
    height: oct-rem(16);

    &--active {
      fill: $oct-theme--danger;
    }
  }
}

.ss-customer-history__trailing-icon  {
  fill: $oct-theme--neutral-50;
  flex: 0 0 oct-rem(32);
  width: oct-rem(32);
  max-width: oct-rem(32);
}
</style>